import React, { useRef, useState } from 'react';
import ChevronDownIcon from '@nokia-csf-uxr/ccfk-assets/ChevronDownIcon';
import IconButton from 'components/IconButton';
import Checkbox from 'components/Checkbox';
import { useSpring, useTransition } from 'react-spring';
import { getContext, useCombinedRefs, isAnimationDisabled } from 'common';
import { CardCollectionContext } from '../CardCollection';
import Styled from './styles';
import CardCore, { CardCoreProps } from './CardCore';

const SPRING_CONFIG = {
  mass: 5,
  tension: 80,
  friction: 10,
  clamp: true
};

export interface CardProps extends CardCoreProps {
  /** Optional prop, however this prop is necessary when using a `Card` within a `CardCollection` component. */
  id?: string;
  /** Content is placed here. */
  children: React.ReactNode;
  /** Controls selected state of the card  (used in conjunction with 'onSelect' prop) */
  selected?: boolean;
  /** onSelect callback - triggered when card is selected (used in conjunction with 'selected' prop). Returns card id. */
  onSelect?: (arg0: React.ChangeEvent, arg1: string) => void;
  /** Controls expansion state of the card (used in conjunction with 'onExpand' prop). */
  expanded?: boolean;
  /** Properties passed to CardExpandedContent. Useful for screen reader properties. */
  cardExpandedProps?: any,
  /** onExpand callback - triggered when card is selected. (used in conjunction with 'expanded' prop). Returns card id.  */
  onExpand?: (any) => void;
  /** Expanded height of the card container (optional). If unspecified, Card will attempt to expand according to content. */
  expandHeight?: number;
  /** Renders expanded content */
  expandedContent?: React.ReactNode;
  /** Renders selection button. Use this to override the default selection checkbox. */
  selectionButton?: React.ReactNode;
  /** Renders expansion button. Use this to override the default expansion button. */
  expansionButton?: React.ReactNode;
  /** Properties to pass to the ExpansionButton. Useful for screen reader. */
  expansionButtonProps?: any;
  /** Configure the 'expand' animation. Uses 'react-spring' see https://react-spring.io for more information. */
  expandAnimationProps?: any;
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** When expanded content is collapsed, it will unmount. Useful for performance considerations. */
  unmountExpandedContent?: boolean;
  /** Toggle hover effect */
  hoverEffect?: boolean;
  /** Toggle focus effect */
  focusEffect?: boolean;
  /** Toggle between visual variants of Card.
   *  'stack' -  shows multiple cards stacked together (for use when indicating that there are multiple cards under this card)
   *  'modal' -  toggles modal styles
   */
  variant?: 'default' | 'stack' | 'modal';
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** @ignore */
  onMouseDown?: React.MouseEventHandler;
  /** @ignore */
  onFocus?: React.FocusEventHandler;
  /** @ignore */
  onBlur?: React.FocusEventHandler;
}

/** Card is a rectangular sheet of content that provides cardCollectionContext and an entry into more in-depth information or view. Card component supports 'expansion' and 'selection' functionality by default. For a basic card without additional functionality, use <SimpleCard /> (See SimpleCard story in Other section). */
const Card = React.forwardRef<HTMLElement, CardProps>((props: CardProps, ref) =>
  getContext(({ RTL: rtl, disableAnimation }) => {
    const containerRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, containerRef);
    const cardCollectionContext: any = React.useContext(CardCollectionContext);
    const [focused, setFocused] = useState(false);
    const {
      id,
      onMouseDown,
      onFocus,
      onBlur,
      onSelect,
      onExpand,
      children,
      expanded,
      selected,
      expandedContent,
      expansionButton,
      expansionButtonProps,
      selectionButton,
      unmountExpandedContent,
      expandHeight,
      expandAnimationProps,
      disableAnimation: propsdisableAnimation,
      variant,
      theme,
      cardExpandedProps,
      ...otherProps
    } = props;
    const resolvedDisableAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);
    /** Animation for expanded content (for mounted content). See `unmountExpandedContent` prop. */
    const mountedAnimation = useSpring(expandAnimationProps || {
      height: expanded ? expandHeight : 0,
      config: SPRING_CONFIG,
      immediate: resolvedDisableAnimation,
    });

    /** Animation for expanded content when it is mounting/unmounting (for unmounted content). See `unmountExpandedContent` prop. */
    const unmountedAnimation = useTransition(
      expanded,
      null,
      expandAnimationProps || {
        from: { height: 0 },
        enter: { height: expandHeight },
        leave: { height: 0 },
        config: SPRING_CONFIG,
        immediate: resolvedDisableAnimation,
      }
    );

    /** Expand button animation */
    const buttonAnimation = useSpring({
      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
      config: SPRING_CONFIG,
      immediate: resolvedDisableAnimation,
    });

    // Event Handler
    const handleEvents = (event) => {
      switch (event.type) {
        case 'mousedown':
          onMouseDown && onMouseDown(event);
          break;
        case 'focus':
          if (event.target === containerRef.current) {
            setFocused(true); // setFocused is to keep track of whether or not CardSelectionButton should be shown on keyboard nav
          }
          onFocus && onFocus(event);
          break;
        case 'blur':
          setFocused(false);
          onBlur && onBlur(event);
          break;
        default:
          break;
      }
    };

    /** Content is unmounted when not expanded */
    const expandedContentUnmounted = () =>
      unmountedAnimation.map(({ item, key, props: styles }) =>
        item && (
          <Styled.CardExpandedContent $cardCollection={cardCollectionContext} key={key} style={styles} $expanded={expanded} {...cardExpandedProps}>
            <Styled.CardExpandedContentInner>{expandedContent}</Styled.CardExpandedContentInner>
          </Styled.CardExpandedContent>
        ));

    /** Content is mounted even when not expanded */
    const expandedContentMounted = () => (
      <Styled.CardExpandedContent $cardCollection={cardCollectionContext} style={mountedAnimation} $expanded={expanded} {...cardExpandedProps}>
        <Styled.CardExpandedContentInner>{expandedContent}</Styled.CardExpandedContentInner>
      </Styled.CardExpandedContent>
    );

    const renderSelectionButton = () => (
      <Styled.CardSelectionButton
        rtl={rtl}
        onFocus={() => {
          setFocused(true);
        }}
        selected={selected}
        focused={focused}
      >
        <Checkbox
          className="ccfk-card-selection-button"
          checked={selected}
          onChange={(event) => {
            onSelect && onSelect(event, id);
          }}
          iconProps={{ 'aria-label': 'select checkbox' }}
        />
      </Styled.CardSelectionButton>
    );
    const renderExpansionButton = () => (
      <Styled.CardFooter expandable={onExpand}>
        <Styled.CardExpansionButton
          style={buttonAnimation}
          // className prop used because RGL uses classnames to exclude elements from triggering drag
          className="ccfk-card-expand-button"
        >
          <IconButton
            onClick={() => {
              cardCollectionContext && cardCollectionContext.expandCard && cardCollectionContext.expandCard(id);
              onExpand && onExpand(combinedRef);
            }}
            {...expansionButtonProps}
          >
            <ChevronDownIcon />
          </IconButton>
        </Styled.CardExpansionButton>
      </Styled.CardFooter>
    );
    return (
      <CardCore
        id={id}
        expanded={expanded}
        ref={combinedRef}
        tabIndex={0}
        role="link"
        focused={focused}
        selected={selected}
        $showSelectableCards={cardCollectionContext && cardCollectionContext.showSelectableCards} // if true shows checkbox indicating card is selectable
        $cardCollection={!!cardCollectionContext}
        variant={variant}
        onSelect={onSelect}
        onMouseDown={handleEvents}
        onFocus={handleEvents}
        onBlur={handleEvents}
        disableAnimation={resolvedDisableAnimation}
        theme={theme}
        {...otherProps}
      >
        {(onSelect || selected) && (selectionButton || renderSelectionButton())}
        <Styled.CardContentContainer $cardCollection={cardCollectionContext} $modal={(variant === 'modal')} rtl={rtl}>
          <Styled.Card rtl={rtl}>{children}</Styled.Card>
        </Styled.CardContentContainer>
        {(onExpand || expanded) && (expansionButton || renderExpansionButton())}
        {expandedContent &&
          (unmountExpandedContent ? expandedContentUnmounted() : expandedContentMounted())}
      </CardCore>
    );
  }));
Card.displayName = 'Card';

Card.defaultProps = {
  hoverEffect: true,
  focusEffect: true,
  id: undefined,
  selected: false,
  onSelect: undefined,
  expanded: false,
  onExpand: undefined,
  expandHeight: 300,
  expandedContent: undefined,
  selectionButton: undefined,
  expansionButton: undefined,
  expansionButtonProps: {},
  unmountExpandedContent: false,
  expandAnimationProps: undefined,
  disableAnimation: undefined,
  variant: 'default',
  theme: {},
  onMouseDown: undefined,
  onFocus: undefined,
  onBlur: undefined,
  cardExpandedProps: {},
};

export default Card;
